import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { handlePinPaginationClick } from './handlePinPaginationClick';
import { trackPinClick } from './trackPinClick';
import { useHotspotClickHandler } from './useHotspotClickHandler';
import { useMaxPinsPerCluster } from './useMaxPinsPerCluster';
import { IPinsLayer } from './usePinsLayer';
import { closeMobileListing } from '../../../../common/actions/listingModal';
import { openOfferPreview } from '../../../../common/actions/openOfferPreview/openOfferPreview';
import { EPinAction, PIN_ACTION_ATTR } from '../../../../common/constants/pinActions';
import { TFeature } from '../../../../common/types/map';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { expandClusterPinFeature } from '../../../../common/utils/features';
import { useMapController } from '../../../controller/useMapController';
import { selectActiveFeature, selectMapBounds } from '../../../selectors';
import { TMapBounds } from '../../../types/mapBounds';

interface IUsePinsClickHandlersParams extends IPinsLayer {
  map: YMaps.Map;
  isResultsAvailable: boolean;
  isDetailsVisible: boolean;
}

export function usePinsClickHandlers({
  map,
  hotspotsLayer,
  balloonManager,
  isResultsAvailable,
  isDetailsVisible,
}: IUsePinsClickHandlersParams): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const { getState } = useStore<IApplicationState>();
  const deviceType = useDeviceType();
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const controller = useMapController();
  const { precision } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);

  React.useEffect(() => {
    const mapElement = map.container.getParentElement();
    const handlePaginationClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const action = target.closest(`[${PIN_ACTION_ATTR}]`)?.getAttribute(PIN_ACTION_ATTR);
      if (action !== EPinAction.PrevOffer && action !== EPinAction.NextOffer) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handlePinPaginationClick(action === EPinAction.PrevOffer ? 'prev' : 'next', {
        dispatch,
        getState,
      });
    };

    mapElement.addEventListener('click', handlePaginationClick, true);

    return () => mapElement.removeEventListener('click', handlePaginationClick, true);
  }, [map, getState, dispatch]);

  // Клик по "веснушкам" -> зум в область веснушки
  const handleHotspotClick = useHotspotClickHandler({ isResultsAvailable, isDetailsVisible });

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('click', handleHotspotClick);

    return () => {
      hotspotsLayer.layer.events.remove('click', handleHotspotClick);
    };
  }, [hotspotsLayer, handleHotspotClick]);

  // Клик по пинам с ценами -> Открытие превью оффера на карте
  const handleBalloonClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const feature = balloonManager.objectManager.objects.getById<TFeature>(objectId);

      if (!feature) {
        return;
      }

      const offerId = feature.properties.offers?.[0]?.cianId;
      const currentZoom = controller.getZoom();
      const zoom = Math.round(currentZoom);

      trackPinClick({ isDetailsVisible, offerId, zoom });

      // Клик по счётчику или повторный клик по выбранному пину сохраняет текущий оффер.
      const activeFeature = selectActiveFeature(getState());
      if (activeFeature?.id === feature.id && activeFeature.precision === precision) {
        return;
      }

      // ObjectManager привязан к центру кластера; превью нужна точка самого пина
      // с пиксельным смещением слота на текущем зуме.
      const expandedFeature = expandClusterPinFeature(feature, currentZoom, maxPinsPerCluster);
      if (!expandedFeature) {
        return;
      }

      // closeMobileListing - мобильная концепция: сбрасывает офферы общего листинга.
      // На десктопе листинг живёт и его трогать нельзя, иначе клик по пину чистит выдачу.
      if (deviceType !== 'desktop') {
        dispatch(closeMobileListing());
      }
      dispatch(
        openOfferPreview(expandedFeature, {
          precision,
          isDetailed: isDetailsVisible,
        }),
      );
    },
    [balloonManager, controller, dispatch, deviceType, precision, isDetailsVisible, getState, maxPinsPerCluster],
  );

  React.useEffect(() => {
    balloonManager.objectManager.objects.events.add('click', handleBalloonClick);

    return () => {
      balloonManager.objectManager.objects.events.remove('click', handleBalloonClick);
    };
  }, [balloonManager, handleBalloonClick]);
}
